import i18next, { type i18n as I18nInstance } from "i18next";

import cn from "./locales/cn.json" with { type: "json" };
import en from "./locales/en.json" with { type: "json" };
import es from "./locales/es.json" with { type: "json" };
import fr from "./locales/fr.json" with { type: "json" };
import it from "./locales/it.json" with { type: "json" };
import ko from "./locales/ko.json" with { type: "json" };
import pt from "./locales/pt.json" with { type: "json" };
import ru from "./locales/ru.json" with { type: "json" };

/**
 * All translation catalogs the app ships with. Ported verbatim from the
 * legacy Expo `legacy/frontend/<country>/app/constants/locales/*.json` files.
 * The legacy `cn.json` file existed but was not registered — it's wired
 * up here.
 */
export const LOCALES = {
  cn: { translation: cn },
  en: { translation: en },
  es: { translation: es },
  fr: { translation: fr },
  it: { translation: it },
  ko: { translation: ko },
  pt: { translation: pt },
  ru: { translation: ru },
} as const;

export type LocaleCode = keyof typeof LOCALES;

export interface CreateI18nOptions {
  /**
   * Desired language, e.g. from `navigator.language` or `Localization.locale`.
   * Only the first two characters are considered; unknown languages fall back
   * to `defaultLanguage`.
   */
  language?: string;
  /** Fallback when the requested language isn't in the catalog. Defaults to "en". */
  defaultLanguage?: LocaleCode;
}

const KNOWN = new Set(Object.keys(LOCALES));

function normalize(language: string | undefined, fallback: LocaleCode): LocaleCode {
  if (!language) return fallback;
  const short = language.slice(0, 2).toLowerCase();
  return (KNOWN.has(short) ? (short as LocaleCode) : fallback);
}

/**
 * Build (but don't share) an i18next instance. Apps call this once at boot
 * and either use the returned instance directly or hand it to
 * `<I18nextProvider>`.
 */
export function createI18n(opts: CreateI18nOptions = {}): I18nInstance {
  const fallback = opts.defaultLanguage ?? "en";
  const language = normalize(opts.language, fallback);
  const instance = i18next.createInstance();
  instance.init({
    compatibilityJSON: "v3",
    resources: LOCALES,
    lng: language,
    fallbackLng: fallback,
    interpolation: { escapeValue: false },
  });
  return instance;
}

export const TRANSLATION_KEYS = Object.freeze(Object.keys(en)) as readonly (keyof typeof en)[];
